// prettier-ignore 改文件包含样式初始化，需要优先加载，否则样式会互相覆盖
import './index.less';
import React, { useEffect } from 'react';
import { OpenPluginType } from '@meitu/saas-editor-core/es/version-types';
import { Layout, LayoutOption, OptionType } from '@meitu/saas-editor-ui/es/layout';
import Collapse from '@/editor/collapse';
import useOpenPlugins, { AI_GENERATE_IMAGE_enter } from '@/editor/collapse/views/more-collapse/hooks/use-open-plugins';
import RenderAIExpendImage from '@/editor/collapse/views/more-collapse/plugins/ai-expend-image/expend-operate/render-ai-expend-image';
import RenderAiModifyImage from '@/editor/collapse/views/more-collapse/plugins/ai-modify-image/operate/render-ai-modify-image';
import OpenImageModal from '@/editor/collapse/views/more-collapse/plugins/open-image-modal/open-image-modal';
import RenderMattingImagePlugin from '@/editor/components/render-matting-image-plugin';
import { StageToolbar } from '@/editor/components/stage-toolbar';
import ExtensionContainer from '@/editor/extensions/components/extension-container/extension-container';
import Header from '@/editor/header';
import Menu from '@/editor/menu';
import Panel from '@/editor/panel';
import { useCommonLogic, useFontMaterials, useProgressHelper } from '@/hooks';
import { editor } from '@/instance';
import { useStoreActions, useStoreState } from '@/store';
import './driver.less';
import { ICustomLayer } from '@meitu/saas-editor-core/es/framework';
import DynamicBgLayer from '@meitu/saas-editor-ui/es/widgets/dynamic-bg-layer/dynamic-bg-layer';
import AIPPTComponentDemo from '@/editor/components/ai-ppt-template-demo/ai-ppt-component-demo';
import AIPPTTemplateDemo from '@/editor/components/ai-ppt-template-demo/aippt-template-demo';
import { events, INIT_EVENT_FINISH } from '@/events-bus';
import useFetchTemplate from '@/hooks/fetch-template';
import {
    isPPTDemoAndShowComponentList,
    isPPTDemoAndShowTemplateComponentList,
} from '@/hooks/fetch-template/editor-mode';

/** 添加舞台工具栏 */
editor.stageToolbar.addNode(<StageToolbar />);

const AppLogic = React.memo(() => {
    useCommonLogic();

    const { openPlugin } = useOpenPlugins();
    const customParams = useStoreState((state) => state.sdkModel.customParams);
    const fetchTemplate = useFetchTemplate();
    const { loadFontMaterials } = useFontMaterials();
    // 顶部进度条
    const progressHelper = useProgressHelper();

    // 注册「打开插件」时间
    useEffect(() => {
        return editor.contextMenu.onOpenPlugin((plugin: OpenPluginType) => {
            openPlugin(plugin);
        });
    }, []);

    // 注册「进入 AI 生图」事件
    useEffect(() => {
        return editor.contextMenu.onAiGenerateImageEnter((entrance: string) => {
            AI_GENERATE_IMAGE_enter(entrance);
        });
    }, []);

    // 设置是否显示高级(AI)操作
    useEffect(() => {
        editor.contextMenu.setShowAdvancedAIOperation(!customParams.hideAiText);
    }, [customParams.hideAiText]);

    useEffect(() => {
        const init = async () => {
            progressHelper.start();
            try {
                // 确保 polyfill 完成
                await editor.textLayer.polyfill();

                // 初始化字体素材
                await loadFontMaterials();

                // 挂载编辑器
                editor.mount();
                // 初始化舞台

                // 获取模版数据
                await fetchTemplate();

                // 设计师编辑器开启网格线插件
                editor.stageGrid.showGridLineOperate(true);
            } catch (error) {
                console.error('初始化失败', error);
            } finally {
                progressHelper.done();
            }
        };
        // 初始化
        init().then(() => {
            editor.wasm.init({
                async success() {
                    await editor.textLayer.loadWasmAssets();
                },
            });
        });
    }, []);

    // 添加动态背景
    useEffect(() => {
        let isInit = false;
        let activeOriginIndex = 0;
        let dynamicBgLayer: ICustomLayer | null = null;

        function addDynamicBgLayer() {
            // const isAiPPT = checkTypeIsAiPPT(getPosterType());
            if (dynamicBgLayer) {
                editor.customLayer.remove(dynamicBgLayer);
            }
            dynamicBgLayer = editor.customLayer.add(
                activeOriginIndex,
                'dynamicBgLayer',
                <DynamicBgLayer editor={editor} isAiPPT={true} />,
                'bottom',
            );
        }
        const initHandler = events.on(INIT_EVENT_FINISH, () => {
            if (isInit) return;
            // 初始化完成，添加自定义图层，添加异步否则 PPT 类型未更新
            setTimeout(() => {
                addDynamicBgLayer();
            }, 0);
            isInit = true;
        });
        const handler = editor.active.onStageChange(({ originIndex }) => {
            // 切换舞台，添加自定义图层
            activeOriginIndex = originIndex;
            addDynamicBgLayer();
        });
        return () => {
            editor.off(handler);
            events.off(initHandler);
        };
    }, []);

    return null;
});

const PosterEditor = () => {
    const currentType = useStoreState((state) => state.collapseModel.currentType);
    const isShowCollapse = useStoreState((state) => state.editorModel.isShowCollapse);
    const setIsShowCollapse = useStoreActions((actions) => actions.editorModel.setIsShowCollapse);
    const isShowPanel = useStoreState((state) => state.editorModel.isShowPanel);

    const openImageModalType = useStoreState((state) => state.pluginModel.openImageModalType);
    const isMatting = useStoreState((state) => state.pluginModel.isMatting);
    const isAiExpendImage = useStoreState((state) => state.pluginModel.isAiExpendImage);
    const isAiModifyImage = useStoreState((state) => state.pluginModel.isAiModifyImage);
    return (
        <>
            {isPPTDemoAndShowTemplateComponentList() ? <AIPPTTemplateDemo editor={editor} /> : null}
            {isPPTDemoAndShowComponentList() ? <AIPPTComponentDemo editor={editor} /> : null}
            <Layout editor={editor} style={{ '--menu-width': '66px' } as React.CSSProperties}>
                <LayoutOption type={OptionType.header}>
                    <Header />
                </LayoutOption>
                <LayoutOption type={OptionType.menu}>
                    <Menu />
                </LayoutOption>
                {isPPTDemoAndShowTemplateComponentList() || isPPTDemoAndShowComponentList() ? null : (
                    <LayoutOption
                        isShowCollapse={isShowCollapse}
                        currentCollapse={currentType as string}
                        type={OptionType.collapse}
                    >
                        <Collapse />
                    </LayoutOption>
                )}
                <LayoutOption
                    isShowCollapse={isShowCollapse}
                    onClick={() => {
                        setIsShowCollapse(!isShowCollapse);
                    }}
                    onResize={(width, height) => {
                        editor.setViewport(width || 1, height || 1);
                    }}
                    type={OptionType.stage}
                >
                    {/* 编辑器容器 */}
                    <div id="editor-container" style={{ position: 'fixed' }}></div>

                    {/* 插件容器 */}
                    <ExtensionContainer useVisibility />
                </LayoutOption>
                <LayoutOption isShowPanel={isShowPanel} type={OptionType.panel}>
                    <Panel />
                </LayoutOption>
                <LayoutOption type={OptionType.extra}></LayoutOption>
                <LayoutOption type={OptionType.cols1}>
                    {/* 单列 */}
                    {/* {isPPT ? <MaterialShortOpera /> : null} */}
                </LayoutOption>
                <LayoutOption type={OptionType.cols2}>
                    {/* 双列 */}
                    {isAiExpendImage ? <RenderAIExpendImage /> : null}
                    {isAiModifyImage ? <RenderAiModifyImage /> : null}
                    {isMatting ? <RenderMattingImagePlugin /> : null}
                </LayoutOption>
                <LayoutOption type={OptionType.cols3}>
                    {/* 三列 */}
                    {openImageModalType ? <OpenImageModal /> : null}
                </LayoutOption>
            </Layout>
            {/* 通用组件 */}
            <CommonComponents />
        </>
    );
};

/** 通用组件 */
const CommonComponents = () => {
    return (
        <>
            <AppLogic />
        </>
    );
};

export default PosterEditor;
